import type { ReactNode } from 'react';

import {
  App,
  Button,
  CrucibleClientProvider,
  FoundryShellHeader,
  HostBridgeProvider,
} from '@volley/crucible-ui';

import { useCookieBoot, type CookieBootPhase } from './auth-v2/cookie-spa-boot.js';
import { beginLogin } from './auth-v2/login.js';
import { beginLogout } from './auth-v2/logout.js';
import { useRuntimeVersion } from './auth-v2/version-probe.js';
import { defaultVersionPollDependencies, useVersionPoll } from './version-poll.js';

function swallowNavigationUnreachable(err: unknown, prefix: string): void {
  if (err instanceof Error && err.message.startsWith(prefix)) return;
  throw err;
}

export interface RootProps {
  renderCloudApp?: () => React.JSX.Element;
}

export function Root({ renderCloudApp = () => <FoundrySpa /> }: RootProps): React.JSX.Element {
  return renderCloudApp();
}

export interface CloudAppShellProps {
  phase: CookieBootPhase;
  showSignOut: boolean;
  onSignOut: () => void;
  children: ReactNode;
}

export function CloudAppShell({
  phase,
  showSignOut,
  onSignOut,
  children,
}: CloudAppShellProps): React.JSX.Element {
  return (
    <div
      data-testid="cloud-app-shell"
      data-phase={phase}
      className="flex h-screen w-screen flex-col overflow-hidden bg-crucible-bg text-crucible-fg"
    >
      <FoundryShellHeader
        chrome="web"
        title="Cloud Foundry"
        testId="cloud-app-shell-header"
        actions={
          <>
            {showSignOut && (
              <Button variant="secondary" size="sm" onClick={onSignOut}>
                Sign out
              </Button>
            )}
          </>
        }
      />
      <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
    </div>
  );
}

interface FoundrySpaProps {
  boot?: ReturnType<typeof useCookieBoot>;
  versionUpdateAvailable?: boolean;
}

function FoundrySpa({ boot, versionUpdateAvailable }: FoundrySpaProps = {}): React.JSX.Element {
  const liveBoot = useCookieBoot();
  const liveVersionPoll = useVersionPoll(defaultVersionPollDependencies());
  const effectiveBoot = boot ?? liveBoot;
  const effectiveVersionUpdateAvailable = versionUpdateAvailable ?? liveVersionPoll.updateAvailable;
  const handleSignOut = (): void => {
    try {
      beginLogout();
    } catch (err) {
      swallowNavigationUnreachable(err, 'beginLogout: unreachable');
    }
  };

  return (
    <CloudAppShell
      phase={effectiveBoot.phase}
      showSignOut={effectiveBoot.phase === 'ready' || effectiveBoot.phase === 'connect-error'}
      onSignOut={handleSignOut}
    >
      <CookieBootContent
        boot={effectiveBoot}
        versionUpdateAvailable={effectiveVersionUpdateAvailable}
      />
      <RuntimeVersionFooter />
    </CloudAppShell>
  );
}

function CookieBootContent(props: {
  boot: ReturnType<typeof useCookieBoot>;
  versionUpdateAvailable: boolean;
}): React.JSX.Element {
  const { boot, versionUpdateAvailable } = props;
  if (boot.phase !== 'ready' || !boot.client) {
    return (
      <PreReadyShell
        phase={boot.phase}
        userLabel={boot.user?.userId}
        errorReason={boot.errorReason}
        versionUpdateAvailable={versionUpdateAvailable}
        beginLogin={beginLogin}
      />
    );
  }
  return (
    <>
      <VersionUpdateLiveRegion updateAvailable={versionUpdateAvailable} />
      <CrucibleClientProvider client={boot.client}>
        <HostBridgeProvider value={null}>
          <App frame="embedded" />
        </HostBridgeProvider>
      </CrucibleClientProvider>
    </>
  );
}

function RuntimeVersionFooter(): React.JSX.Element | null {
  const v = useRuntimeVersion();
  if (v.kind === 'loading') return null;
  const label =
    v.kind === 'ready'
      ? `Crucible v${v.info.runtimeVersion} (${v.info.mode})`
      : 'Crucible runtime unknown';
  return (
    <footer
      data-testid="version-info"
      data-version-kind={v.kind}
      style={{
        position: 'fixed',
        bottom: 4,
        right: 8,
        fontSize: 11,
        opacity: 0.5,
        fontFamily: 'monospace',
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      {label}
    </footer>
  );
}

function PreReadyShell(props: {
  phase: string;
  userLabel?: string;
  errorReason?: string;
  versionUpdateAvailable: boolean;
  beginLogin: () => void;
}): React.JSX.Element {
  const title =
    props.phase === 'connect-error'
      ? 'Could not open Cloud Foundry'
      : props.phase === 'probing'
        ? 'Opening Cloud Foundry'
        : 'Sign in to Cloud Foundry';

  return (
    <main className="flex h-full items-center justify-center px-6">
      <section className="w-full max-w-md rounded-lg border border-white/10 bg-crucible-surface p-6 shadow-2xl">
        <h1 data-testid="bg-0-placeholder" className="text-xl font-semibold">
          {title}
        </h1>

        <VersionUpdateLiveRegion updateAvailable={props.versionUpdateAvailable} />

        <div data-testid="boot-phase" className="mt-3 text-xs text-white/35">
          {props.phase}
        </div>

        {props.phase === 'signed-out' || props.phase === 'idle' ? (
          <Button
            data-testid="sign-in-button"
            className="mt-5"
            onClick={() => {
              try {
                props.beginLogin();
              } catch (err) {
                swallowNavigationUnreachable(err, 'beginLogin: unreachable');
              }
            }}
          >
            Sign in
          </Button>
        ) : null}

        {props.phase === 'probing' ? (
          <p data-testid="auth-status" className="mt-4 text-sm text-white/55">
            Checking session...
          </p>
        ) : null}

        {props.phase === 'connect-error' ? (
          <p data-testid="auth-status" className="mt-4 text-sm leading-6 text-white/55">
            Session restore failed: {props.errorReason ?? 'unknown error'}
          </p>
        ) : null}

        {props.userLabel ? (
          <p className="mt-3 truncate text-xs text-white/35">{props.userLabel}</p>
        ) : null}
      </section>
    </main>
  );
}

function VersionUpdateLiveRegion(props: { updateAvailable: boolean }): React.JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={props.updateAvailable ? 'version-update-banner' : undefined}
      className={props.updateAvailable ? 'mt-4 text-sm text-white/70' : undefined}
    >
      {props.updateAvailable ? (
        <>
          <span>A new version of Foundry is available.</span>{' '}
          <Button
            variant="ghost"
            size="sm"
            data-testid="version-update-reload"
            onClick={() => {
              window.location.reload();
            }}
            className="underline decoration-white/30 underline-offset-4 hover:decoration-white/70"
          >
            Reload to update
          </Button>
        </>
      ) : null}
    </div>
  );
}
